import EventEmitter from "events";
import { UIService } from "../UIService";
import { sleep } from "../../utils/reflection";

export interface DataChannelConfiguration {
  name: string;
}

export interface ObjectStateManager {
  updateState(dgram: any): void;
  getChildStateManager(name: string): ObjectStateManager | null;
  getUIService(): UIService;
  getActorId(): string;
}

export abstract class BaseDataChannel extends EventEmitter {
  public dataChannel: RTCDataChannel | null = null;
  public stateManager: ObjectStateManager | null = null;
  private chunkingBuffer: Uint8Array | null = null;
  private connected = false;
  public abstract getConfiguration(): DataChannelConfiguration;
  public abstract onMessage(ev: MessageEvent): void;

  public connect(dc: RTCDataChannel, stateManager: ObjectStateManager) {
    this.dataChannel = dc;
    this.stateManager = stateManager;
    const normalHandler = (ev: MessageEvent) => {
      const buffer = ev.data as ArrayBuffer;
      if (buffer.byteLength >= 4) {
        const markerView = new Uint8Array(buffer.slice(0, 4));
        if (markerView[0] === 0xff && markerView[1] === 0xff && markerView[2] === 0xff) {
          // This is a chunking marker
          const totalChunks = markerView[3];
          // const totalSize = buffer.byteLength - 4 + (totalChunks - 1) * 65536;
          // console.log('Receiving chunked data of size', totalSize, 'in', totalChunks, 'chunks');
          this.chunkingBuffer = new Uint8Array(totalChunks * 65536);
          // this.chunkingBuffer.set(new Uint8Array(buffer.slice(4)), 0);
          let bytesReceived = 0;
          let chunksRemaining = totalChunks;
          const receiveChunk = (event: MessageEvent) => {
            const chunk = new Uint8Array(event.data);
            this.chunkingBuffer!.set(chunk, bytesReceived);
            bytesReceived += chunk.length;
            chunksRemaining -= 1;
            // console.log('Received chunk of', chunk.length, 'bytes');
            if (chunksRemaining === 0) {
              // console.log('All chunks received, total size', bytesReceived);
              this.dataChannel!.onmessage = normalHandler;
              this.onMessage(new MessageEvent('message',
                {
                  data: this.chunkingBuffer!.buffer.slice(0, bytesReceived)
                }
              ));
              this.chunkingBuffer = null;
            } else {
              this.dataChannel!.onmessage = receiveChunk;
            }
          };
          if (chunksRemaining > 0) {
            this.dataChannel!.onmessage = receiveChunk;
          } else {
            // No more chunks, process the data
            this.onMessage(new MessageEvent('message', { data: this.chunkingBuffer.buffer }));
            this.chunkingBuffer = null;
          }
          return;
        }
      }
      this.onMessage(ev);
    };
    this.dataChannel.onmessage = normalHandler;
    this.onConnected();
    this.connected = true;
  }

  public isConnected() {
    return this.connected && this.dataChannel !== null && this.dataChannel.readyState === 'open';
  }

  protected onConnected() { }

  public sendBytes(buffer: ArrayBuffer) {
    this.sendUint8Array(new Uint8Array(buffer));
  }

  public async sendUint8Array(data: Uint8Array) {
    try {
      if (!this.isConnected()) {
        return;
      }
      if (data.length < 128000) {
        this.dataChannel!.send(data as any);
      } else {
        // send in 64kb chunks
        const chunkSize = 65536;
        // send a marker first
        const chunks = Math.ceil(data.length / chunkSize);
        const marker = new Uint8Array([0xff, 0xff, 0xff, chunks]);
        this.dataChannel!.send(marker.buffer);
        let offset = 0;
        while (offset < data.length) {
          await sleep(10);
          const end = Math.min(offset + chunkSize, data.length);
          this.dataChannel!.send(data.slice(offset, end).buffer);
          // console.log('Sent chunk', offset, end);
          offset = end;
        }
      }
    } catch (e) {
      console.error('Error sending data: ', e);
    }
  }

}

export abstract class BaseFloatDataChannel extends BaseDataChannel {

  public send(data: number[]) {
    const buffer = new ArrayBuffer(data.length * 4);
    const view = new Float32Array(buffer);
    view.set(data);
    if (this.isConnected()) {
      this.dataChannel!.send(new Uint8Array(buffer));
    } else {
      console.warn('Data channel is not connected, cannot send data');
    }
  }

  public onMessage(ev: MessageEvent) {
    const data = new Float32Array(ev.data);
    const dataArr = Array.from(data);
    this.emit('data', dataArr);
    this.onData(dataArr);
  }

  public onData(data: number[]): void {
  }
}

export abstract class BaseJsonDataChannel extends BaseDataChannel {
  public send(data: any) {
    if (this.isConnected()) {
      this.dataChannel!.send(JSON.stringify(data));
    }
  }
  public onMessage(ev: MessageEvent) {
    try {
      const data = JSON.parse(ev.data);
      // console.log('received data: ', data);
      const firstKey = Object.keys(data)[0];
      if (this.stateManager) {
        if (firstKey.includes('.')) {
          // console.log('received data: ', data);
          const [childName, propertyName] = firstKey.split('.');
          const childStateManager = this.stateManager.getChildStateManager(childName);
          if (childStateManager) {
            const childData = { [propertyName]: data[firstKey] };
            childStateManager.updateState(childData);
          } else {
            console.warn(`Child state manager ${childName} not found`);
          }
        } else {
          // console.log('received data: ', data);
          this.stateManager.updateState(data);
        }
      }
    } catch (e) {
      console.error('Error parsing JSON data: ', e);
    }
  }
}